import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { SalesAndPlannedProducts } from './components/SalesAndPlannedProducts';
import { SalesChannels } from './components/SalesChannels';
import { SalesStages } from './components/SalesStages';
import { channelsInitialValues, prefix, SALES_NAMES, salesStagesInitialValues } from './utils/form';

export const SalesPlanSection: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('marketingAndSales.title')}
          badgeText={t('marketingAndSales.sales.title')}
          section={FOR_BANK_SECTIONS.MARKETING_AND_SALES}
        />
      </FormSectionCard>
      {/* 5.1.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.goals')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.GOALS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.GOALS)}
          name={concatWithDot(prefix, SALES_NAMES.GOALS)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 5.1.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.stages.title')}
        collapsible
        aiFieldName={concatWithDot(prefix, SALES_NAMES.STAGES)}
        aiFieldType="complex"
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, SALES_NAMES.STAGES)}
          initialItem={salesStagesInitialValues}
          buttonText={t('marketingAndSales.sales.stages.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix, index) => <SalesStages name={namePrefix} disabled={!canAccess} index={index} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 5.1.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.affectFactors')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.AFFECT_FACTORS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.AFFECT_FACTORS)}
          name={concatWithDot(prefix, SALES_NAMES.AFFECT_FACTORS)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 5.1.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.performanceIndicators')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.PERFORMANCE_INDICATORS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.PERFORMANCE_INDICATORS)}
          name={concatWithDot(prefix, SALES_NAMES.PERFORMANCE_INDICATORS)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 5.1.5 */}
      <SalesAndPlannedProducts disabled={!canAccess} />
      {/* 5.1.6 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.channels.title')}
        collapsible
        aiFieldName={concatWithDot(prefix, SALES_NAMES.CHANNELS)}
        aiFieldType="complex"
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, SALES_NAMES.CHANNELS)}
          initialItem={channelsInitialValues}
          buttonText={t('marketingAndSales.sales.channels.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <SalesChannels name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 5.1.7 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.detailedSalesChannel')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.DETAILED_SALES_CHANNEL)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.DETAILED_SALES_CHANNEL)}
          name={concatWithDot(prefix, SALES_NAMES.DETAILED_SALES_CHANNEL)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 5.1.8 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.salesTeamStructure')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.SALES_TEAM_STRUCTURE)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.SALES_TEAM_STRUCTURE)}
          name={concatWithDot(prefix, SALES_NAMES.SALES_TEAM_STRUCTURE)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 5.1.9 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingAndSales.sales.motivationSystem')}
        aiFieldName={concatWithDot(prefix, SALES_NAMES.MOTIVATION_SYSTEM)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, SALES_NAMES.MOTIVATION_SYSTEM)}
          name={concatWithDot(prefix, SALES_NAMES.MOTIVATION_SYSTEM)}
          minRows={4}
          disabled={!canAccess}
        />
      </FormSectionCard>
    </>
  );
};
